// ProjectServiceTypeSelect — division picker + service picker, both fed from
// the database (service_divisions / service_catalog). Run A, 2026-08-18.
//
// What changed and why: this used to be a <select> over a hardcoded frontend
// array with a free-text "Other" box that wrote whatever was typed straight
// into the stored column. That escape hatch is how the stored vocabulary grew
// from 56 variants to 77 AFTER the dropdown shipped. Now:
//
//  - `value` is a service_code from service_catalog, never a display string.
//  - The division picker doubles as the old "Category" field. Category was a
//    free-text input labelled "Optional", which is why 66 rows have none.
//  - The escape hatch survives, but it can only ever write service_code =
//    'OTHER' plus required words in scope_text. It NEVER writes free text into
//    a controlled column, and the row routes for classification.
import { useEffect } from 'react';
import { OTHER_SERVICE_CODE, useServiceCatalog } from './hooks/use-service-catalog';

interface Props {
  /** service_divisions.division_id, or '' when nothing is chosen yet. */
  divisionId: string;
  onDivisionChange: (next: string) => void;
  /** service_catalog.service_code, or '' when nothing is chosen yet. */
  value: string;
  onChange: (next: string) => void;
  /** Required words when the OTHER escape hatch is chosen. */
  scopeText: string;
  onScopeTextChange: (next: string) => void;
  selectClassName?: string;
  inputClassName?: string;
  id?: string;
  required?: boolean;
}

export function ProjectServiceTypeSelect({
  divisionId,
  onDivisionChange,
  value,
  onChange,
  scopeText,
  onScopeTextChange,
  selectClassName,
  inputClassName,
  id,
  required,
}: Props) {
  const { divisions, servicesForDivision, divisionForService, isLoading, error } =
    useServiceCatalog();

  // Editing an existing row: the stored service_code implies its division, so
  // the division picker fills itself in rather than looking empty.
  useEffect(() => {
    if (!divisionId && value && value !== OTHER_SERVICE_CODE) {
      const d = divisionForService(value);
      if (d) onDivisionChange(d);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, divisionId, isLoading]);

  const options = servicesForDivision(divisionId);

  return (
    <div className="space-y-2">
      <select
        aria-label="Division"
        className={selectClassName}
        value={divisionId}
        disabled={isLoading}
        onChange={(e) => {
          onDivisionChange(e.target.value);
          onChange(''); // a new division invalidates the chosen service
        }}
      >
        <option value="">{isLoading ? 'Loading divisions…' : 'Pick a division…'}</option>
        {divisions.map((d) => (
          <option key={d.division_id} value={d.division_id}>
            {d.display_name}
          </option>
        ))}
      </select>

      {/* ⛔ The same control the building's standing lines render. One picker. */}
      <ServiceCodeSelect
        id={id}
        required={required}
        options={options}
        value={value}
        onChange={onChange}
        scopeText={scopeText}
        onScopeTextChange={onScopeTextChange}
        placeholder={divisionId ? 'Pick the work…' : 'Pick a division first'}
        showOther={!!divisionId}
        disabled={isLoading || !divisionId}
        selectClassName={selectClassName}
        inputClassName={inputClassName}
      />

      {error && <p className="text-[11px] text-st-danger-fg">{error}</p>}
    </div>
  );
}

// ============================================================================
// ⛔ ONE PICKER, ONE DOOR, ONE SET OF WORDS.
//
// The service select and the "I don't see it" escape hatch, on their own, so the
// one-off project screen and a building's STANDING service lines render the same
// control. There is no second picker for this question, no second escape hatch
// and no second phrasing — the words below are the words that shipped on
// 2026-08-18 and they are not rewritten for a new caller. Ben, 2026-09-16.
// ============================================================================
export function ServiceCodeSelect({
  options,
  value,
  onChange,
  scopeText,
  onScopeTextChange,
  placeholder,
  showOther,
  disabled,
  selectClassName,
  inputClassName,
  id,
  required,
}: {
  options: readonly { service_code: string; display_name: string }[];
  value: string;
  onChange: (next: string) => void;
  /** Required words when the escape hatch is chosen. */
  scopeText: string;
  onScopeTextChange: (next: string) => void;
  placeholder: string;
  /** The door is offered only once there is a list to not-see-it in. */
  showOther: boolean;
  disabled?: boolean;
  selectClassName?: string;
  inputClassName?: string;
  id?: string;
  required?: boolean;
}) {
  const isOther = value === OTHER_SERVICE_CODE;

  return (
    <>
      <select
        id={id}
        required={required}
        aria-label="Service"
        className={selectClassName}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder}</option>
        {options.map((s) => (
          <option key={s.service_code} value={s.service_code}>
            {s.display_name}
          </option>
        ))}
        {showOther && <option value={OTHER_SERVICE_CODE}>I don&apos;t see it</option>}
      </select>

      {isOther && (
        <input
          type="text"
          className={inputClassName}
          value={scopeText}
          onChange={(e) => onScopeTextChange(e.target.value)}
          placeholder="Describe it in your own words — this routes for classification"
          required
          autoFocus
        />
      )}
    </>
  );
}

/**
 * What kind of work a building's STANDING service line is — the monthly clean,
 * the day porter, the trash pull.
 *
 * No division picker: standing work is one division on its own axis, so asking
 * which division would be asking a question with one answer. The list is
 * narrowed from the catalogue already fetched, never by filtering the fetch.
 */
export function RecurringServiceSelect({
  value,
  onChange,
  scopeText,
  onScopeTextChange,
  selectClassName,
  inputClassName,
  id,
  required,
}: {
  /** service_assignments.service_code, or '' when nothing is chosen yet. */
  value: string;
  onChange: (next: string) => void;
  scopeText: string;
  onScopeTextChange: (next: string) => void;
  selectClassName?: string;
  inputClassName?: string;
  id?: string;
  required?: boolean;
}) {
  const { recurringServices, isLoading, error } = useServiceCatalog();

  return (
    <div className="space-y-2">
      <ServiceCodeSelect
        id={id}
        required={required}
        options={recurringServices}
        value={value}
        onChange={onChange}
        scopeText={scopeText}
        onScopeTextChange={onScopeTextChange}
        placeholder={isLoading ? 'Loading the list…' : 'Pick the work…'}
        showOther={!isLoading && recurringServices.length > 0}
        disabled={isLoading}
        selectClassName={selectClassName}
        inputClassName={inputClassName}
      />
      {/* ⛔ An empty option list is not an empty answer (TCSD-0129 family): say
          so rather than render a control with nothing in it. */}
      {!isLoading && !error && recurringServices.length === 0 && (
        <p className="text-[11px] text-tcs-text-secondary">
          No standing-work types are set up, so there is nothing to choose yet.
        </p>
      )}
      {error && <p className="text-[11px] text-st-danger-fg">{error}</p>}
    </div>
  );
}
